import moment from 'moment/moment';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';

export const countryApi = createApi({
  tagTypes: [
    'COUNTRY',
    'COUNTRY_LIST',
    'COUNTRY_TAGS',
    'COUNTRY_PRICING_RULES',
    'COUNTRY_CLIENT_TYPE_PRICING_RULES',
    'COUNTRY_CITY_SHIPPING_RATE',
    'COUNTRY_SHIPPING_RATE',
    'COUNTRY_WAREHOUSE_LIST',
  ],
  reducerPath: 'countryApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    countryList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/wrd/api/country/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        if (Array.isArray(response?.data?.content)) {
          response.data.content = (response?.data?.content || []).map((item: any) => {
            return {
              ...item,
              formattedLastModifiedDate: {
                full: moment(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment(item.lastModifiedDate).format('DD.MM.YYYY'),
              },
              key: item.id,
              label: item.name,
              value: item.id,
            };
          });
        }
        return response;
      },
      providesTags: () => ['COUNTRY_LIST'],
    }),
    countryManagement: builder.query<any, any>({
      query: ({ id }) => {
        return {
          url: `/wrd/api/country/management/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response.data;
      },
      providesTags: () => ['COUNTRY'],
    }),
    countryManagementUpdate: builder.mutation<any, any>({
      query: (body) => ({
        url: '/wrd/api/country/management',
        method: 'put',
        body,
      }),
      invalidatesTags: () => ['COUNTRY'],
    }),
    countryDetailById: builder.query<any, { id: string }>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/country/${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['COUNTRY'],
    }),
    countryDetailsUpdate: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/country/details',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['COUNTRY', 'COUNTRY_LIST', 'COUNTRY_TAGS'],
    }),
    countryTags: builder.query<any, void>({
      query: () => ({
        url: '/wrd/api/country/tags',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || [];
      },
      providesTags: () => ['COUNTRY_TAGS'],
    }),
    // Pricing Rules
    countryPricingRule: builder.query<any, any>({
      query: ({ countryId }) => {
        return {
          url: `/wrd/api/country/${countryId}/pricing-rules`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || {};
      },
      providesTags: () => ['COUNTRY_PRICING_RULES'],
    }),
    countryPricingRuleUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/pricing-rules`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_PRICING_RULES', 'COUNTRY'],
    }),
    countryPricingRuleClientTypeList: builder.query<any, any>({
      query: ({ countryId }) => {
        return {
          url: `/wrd/api/country/${countryId}/client-type`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['COUNTRY_CLIENT_TYPE_PRICING_RULES'],
    }),
    countryClientTypePricingRuleAdd: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/client-type-pricing-rules`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_CLIENT_TYPE_PRICING_RULES', 'COUNTRY_PRICING_RULES', 'COUNTRY'],
    }),
    countryClientTypePricingRuleUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/client-type-pricing-rules`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_CLIENT_TYPE_PRICING_RULES', 'COUNTRY_PRICING_RULES', 'COUNTRY'],
    }),
    countryClientTypePricingRuleDelete: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/client-type-pricing-rules`,
          method: 'delete',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_CLIENT_TYPE_PRICING_RULES', 'COUNTRY_PRICING_RULES', 'COUNTRY'],
    }),

    // Shipping
    countryCityShippingRateAdd: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/shipping-rate/city`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_CITY_SHIPPING_RATE', 'COUNTRY'],
    }),
    countryCityShippingRateUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/shipping-rate/city`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_CITY_SHIPPING_RATE', 'COUNTRY'],
    }),
    countryCityShippingRatesList: builder.query<any, any>({
      query: ({ countryId }) => {
        return {
          url: `/wrd/api/country/shipping-rate/city?countryId=${countryId}`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['COUNTRY_CITY_SHIPPING_RATE'],
    }),
    countryCityShippingRateDelete: builder.mutation<any, any>({
      query: ({ id }) => {
        return {
          url: `/wrd/api/country/shipping-rate/city?id=${id}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['COUNTRY_CITY_SHIPPING_RATE', 'COUNTRY'],
    }),
    countryShippingRateAdd: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/shipping-rate`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_SHIPPING_RATE', 'COUNTRY'],
    }),
    countryShippingRateUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/country/shipping-rate`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['COUNTRY_SHIPPING_RATE', 'COUNTRY'],
    }),
    countryShippingRatesList: builder.query<any, any>({
      query: ({ countryId }) => {
        return {
          url: `/wrd/api/country/shipping-rate?countryId=${countryId}`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.name,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['COUNTRY_SHIPPING_RATE'],
    }),
    countryShippingRateDelete: builder.mutation<any, any>({
      query: ({ id }) => {
        return {
          url: `/wrd/api/country/shipping-rate?id=${id}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['COUNTRY_SHIPPING_RATE', 'COUNTRY'],
    }),
    countryWarehouseList: builder.query<any, any>({
      query: ({ countryId }) => {
        return {
          url: `/wrd/api/country/warehouse?countryId=${countryId}`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => {
        return response.data;
      },
      providesTags: () => ['COUNTRY_WAREHOUSE_LIST'],
    }),
    countryWarehouseCreate: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `/wrd/api/country/warehouse`,
          method: 'post',
          body: data,
        };
      },
      invalidatesTags: () => ['COUNTRY_WAREHOUSE_LIST', 'COUNTRY'],
    }),
    countryWarehouseUpdate: builder.mutation<any, any>({
      query: (data) => {
        return {
          url: `/wrd/api/country/warehouse`,
          method: 'put',
          body: data,
        };
      },
      invalidatesTags: () => ['COUNTRY_WAREHOUSE_LIST', 'COUNTRY'],
    }),
    countryAllWarehouseList: builder.query<any, void>({
      query: () => {
        return {
          url: `/wrd/api/country/warehouse/all`,
          method: 'get',
        };
      },
      transformResponse: (response: any) => {
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: [item.name, item.country?.name, item.state, item.city, item.streetAddress, item.zip]
            .filter(Boolean)
            .join(', '),
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['COUNTRY_WAREHOUSE_LIST'],
    }),
  }),
});

export const {
  useCountryDetailByIdQuery,
  useCountryListQuery,
  useCountryTagsQuery,
  useCountryDetailsUpdateMutation,
  useCountryManagementUpdateMutation,
  useCountryPricingRuleUpdateMutation,
  useCountryPricingRuleQuery,
  useCountryPricingRuleClientTypeListQuery,
  useCountryManagementQuery,
  useCountryClientTypePricingRuleAddMutation,
  useCountryClientTypePricingRuleUpdateMutation,
  useCountryClientTypePricingRuleDeleteMutation,
  useCountryCityShippingRateAddMutation,
  useCountryCityShippingRateUpdateMutation,
  useCountryCityShippingRatesListQuery,
  useCountryCityShippingRateDeleteMutation,
  useCountryShippingRateAddMutation,
  useCountryShippingRateUpdateMutation,
  useCountryShippingRatesListQuery,
  useCountryShippingRateDeleteMutation,
  useCountryWarehouseCreateMutation,
  useCountryWarehouseUpdateMutation,
  useCountryWarehouseListQuery,
  useCountryAllWarehouseListQuery,
} = countryApi;
